/**
 * useWorkflowGates — React binding for WorkflowEngine.
 *
 * Parses the CartDto JSON payloads into a `CartSnapshot`, instantiates the
 * WorkflowEngine with EntityRules from ConfigContext, evaluates, and memoizes
 * the resulting GateReport.
 *
 * Consumers: ReviewStep (commit gating) + GateReport (UI rendering).
 */
import { useMemo } from 'react';
import type { CartDto, ReviewSummary } from '@retail-ops/shared-types';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import { useConfig } from '@/contexts/ConfigContext';
import { beneficiaryParties, buildTestIdentityGate, readTestIdentitySettings } from '@/utils/testIdentity';
import {
  WorkflowEngine,
  type CartSnapshot,
  type GateReport,
  type PersonSnapshot,
  type ProductSnapshot,
} from '@/services/WorkflowEngine';

export interface UseWorkflowGatesResult {
  report: GateReport;
  snapshot: CartSnapshot;
}

function parsePeople(cart: CartDto): PersonSnapshot[] {
  if (!cart.peopleJson) return [];
  try {
    const raw = JSON.parse(cart.peopleJson) as unknown;
    let arr: unknown[] = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
      arr = (raw as { people: unknown[] }).people;
    }
    return arr
      .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
      .map((p, idx) => {
        const extra = (p.extra && typeof p.extra === 'object') ? (p.extra as Record<string, unknown>) : undefined;
        const out: PersonSnapshot = {
          id: typeof p['id'] === 'string' ? (p['id'] as string) : String(idx),
          role: typeof p['role'] === 'string' ? (p['role'] as string) : 'primary',
        };
        const assignStr = (k: keyof PersonSnapshot, src: string): void => {
          if (typeof p[src] === 'string' && (p[src] as string).length > 0) {
            (out as unknown as Record<string, unknown>)[k] = p[src];
          }
        };
        assignStr('firstName', 'firstName');
        assignStr('middleName', 'middleName');
        assignStr('lastName', 'lastName');
        assignStr('fullName', 'fullName');
        assignStr('name2', 'name2');
        assignStr('dob', 'dob');
        assignStr('taxId', 'taxId');
        assignStr('tinCode', 'tinCode');
        assignStr('noTinClearedKey', 'noTinClearedKey');
        assignStr('cifId', 'cifId');   // existing-customer exemptions (Bill 2026-07-23)
        assignStr('customerType', 'customerType');
        assignStr('addr', 'addr');
        assignStr('city', 'city');
        assignStr('state', 'state');
        assignStr('zip', 'zip');
        assignStr('citizenshipStatus', 'citizenshipStatus');
        assignStr('countryOfCitizenship', 'countryOfCitizenship');
        assignStr('cipStatus', 'cipStatus');
        if (typeof p['cddExempt'] === 'boolean') out.cddExempt = p['cddExempt'] as boolean;
        if (typeof p['cddExemptReasonCode'] === 'string') out.cddExemptReasonCode = p['cddExemptReasonCode'] as string;
        if (extra) {
          out.extra = extra;
          // Promote commonly-used extras onto the snapshot for easy access.
          if (typeof extra['idTypeCode'] === 'string') out.idTypeCode = extra['idTypeCode'] as string;
          if (typeof extra['idNumber'] === 'string') out.idNumber = extra['idNumber'] as string;
          if (typeof extra['idIssuer'] === 'string') out.idIssuer = extra['idIssuer'] as string;
          if (typeof extra['idIssueDate'] === 'string') out.idIssueDate = extra['idIssueDate'] as string;
          if (typeof extra['idExpiryDate'] === 'string') out.idExpiryDate = extra['idExpiryDate'] as string;
          if (typeof extra['ofacStatus'] === 'string') {
            out.ofacStatus = extra['ofacStatus'] as PersonSnapshot['ofacStatus'];
          }
          if (typeof extra['custodianCifId'] === 'string') out.custodianCifId = extra['custodianCifId'] as string;
          if (typeof extra['protectedPersonCifId'] === 'string') {
            out.protectedPersonCifId = extra['protectedPersonCifId'] as string;
          }
          if (typeof extra['probateDocOnFile'] === 'boolean') {
            out.probateDocOnFile = extra['probateDocOnFile'] as boolean;
          }
          if (typeof extra['courtOrderOnFile'] === 'boolean') {
            out.courtOrderOnFile = extra['courtOrderOnFile'] as boolean;
          }
        }
        return out;
      });
  } catch {
    return [];
  }
}

function parseProducts(cart: CartDto): ProductSnapshot[] {
  if (!cart.productsJson) return [];
  try {
    const raw = JSON.parse(cart.productsJson) as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
      .map((p) => {
        const out: ProductSnapshot = {
          code: typeof p['code'] === 'string' ? (p['code'] as string) : '',
        };
        if (typeof p['acctType'] === 'string') out.acctType = p['acctType'] as string;
        if (typeof p['qty'] === 'number') out.qty = p['qty'] as number;
        if (typeof p['openingDeposit'] === 'number') out.openingDeposit = p['openingDeposit'] as number;
        if (typeof p['odpEnabled'] === 'boolean') out.odpEnabled = p['odpEnabled'] as boolean;
        if (p['featureSelections'] && typeof p['featureSelections'] === 'object') {
          out.featureSelections = p['featureSelections'] as Record<string, unknown>;
        }
        return out;
      })
      .filter((p) => p.code.length > 0);
  } catch {
    return [];
  }
}

function parseMetadata(cart: CartDto): Record<string, unknown> {
  if (!cart.metadataJson) return {};
  try {
    const raw = JSON.parse(cart.metadataJson);
    return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function extractEntityType(meta: Record<string, unknown>): CustomerType | undefined {
  const o = meta['ownership'];
  if (o && typeof o === 'object') {
    const v = (o as Record<string, unknown>)['entityType'];
    if (typeof v === 'string') return v as CustomerType;
  }
  return undefined;
}

function extractIsJoint(meta: Record<string, unknown>): boolean {
  const o = meta['ownership'];
  if (o && typeof o === 'object') {
    const v = (o as Record<string, unknown>)['acctOwnership'];
    if (typeof v === 'string' && v.toLowerCase() === 'joint') return true;
  }
  return false;
}

export function useWorkflowGates(
  cart: CartDto,
  reviewSummary?: ReviewSummary | null,
): UseWorkflowGatesResult {
  const { config } = useConfig();

  return useMemo(() => {
    const meta = parseMetadata(cart);
    const people = parsePeople(cart);
    const products = parseProducts(cart);
    const entityType = extractEntityType(meta);
    const isJoint = extractIsJoint(meta) || people.some((p) => p.role === 'joint');

    const complianceAnswers = meta['complianceAnswers'] && typeof meta['complianceAnswers'] === 'object'
      ? (meta['complianceAnswers'] as Record<string, boolean>)
      : {};

    const beneficialOwners = meta['beneficialOwners'] && Array.isArray(meta['beneficialOwners'])
      ? (meta['beneficialOwners'] as Array<{ name: string; percent: number; isController: boolean }>)
      : [];

    const snapshot: CartSnapshot = {
      cartId: cart.cartId,
      entityType,
      isJoint,
      people,
      products,
      documents: [], // TODO(v3.1): wire DocumentChecklist port.
      complianceAnswers,
      beneficialOwners,
      ...(cart.riskReviewStatus ? { riskReviewStatus: cart.riskReviewStatus } : {}),
      metadata: meta,
    };

    // P0 fix 2026-04-27: ConfigBundle now exposes entityRules + the junction
    // arrays (entityComplianceGates etc.) as flat top-level fields; pass both
    // through so the engine can join them by customerType.
    // BACKLOG-0018 Gap 2 (2026-05-26): when reviewSummary is supplied, the
    // engine post-applies per-(customerType × gateKey) admin enforcement
    // strategies (warn-only / deferred-N-days-post-acctadd / officer-override).
    const rules = config.entityRules ?? [];
    const complianceGates = config.entityComplianceGates ?? [];
    const subtypeGates = reviewSummary?.subtypeGates ?? [];
    const engine = new WorkflowEngine({ rules, complianceGates, subtypeGates });
    const report = engine.evaluate(snapshot);
    // Test-identity rules (Bill 2026-09-15) — every person and beneficiary on a
    // test application must be a test customer. Server commit validation mirrors it.
    const identityGate = buildTestIdentityGate(
      [...people, ...beneficiaryParties(meta)],
      readTestIdentitySettings(config.systemSettings),
      entityType,
    );
    if (identityGate) {
      report.gates.unshift(identityGate);
      report.blockerCount += 1;
      report.canCommit = false;
    }
    return { report, snapshot };
  }, [cart, config, reviewSummary]);
}
